import { api } from './api';
import type {
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskFilters,
  TasksResponse,
  TaskResponse,
  TasksSimpleResponse,
} from '../types/task';

// Base URL for tasks
const TASKS_URL = '/tasks';

/**
 * Get all tasks with filters and pagination
 */
export const getTasks = async (filters?: TaskFilters): Promise<TasksResponse> => {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.status?.length) {
      params.append('status', filters.status.join(','));
    }
    if (filters.priority?.length) {
      params.append('priority', filters.priority.join(','));
    }
    if (filters.clientId) {
      params.append('clientId', filters.clientId);
    }
    if (filters.dueDate) {
      params.append('dueDate', filters.dueDate);
    }
    if (filters.dueBefore) {
      params.append('dueBefore', filters.dueBefore);
    }
    if (filters.dueAfter) {
      params.append('dueAfter', filters.dueAfter);
    }
    if (filters.overdue) {
      params.append('overdue', 'true');
    }
    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    if (filters.sortOrder) {
      params.append('sortOrder', filters.sortOrder);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }
  }

  const queryString = params.toString();
  const url = queryString ? `${TASKS_URL}?${queryString}` : TASKS_URL;

  const response = await api.get<TasksResponse>(url);
  return response.data;
};

/**
 * Get today's tasks
 */
export const getTodayTasks = async (): Promise<TasksSimpleResponse> => {
  const response = await api.get<TasksSimpleResponse>(`${TASKS_URL}/today`);
  return response.data;
};

/**
 * Get overdue tasks
 */
export const getOverdueTasks = async (): Promise<TasksSimpleResponse> => {
  const response = await api.get<TasksSimpleResponse>(`${TASKS_URL}/overdue`);
  return response.data;
};

/**
 * Get a single task by ID
 */
export const getTask = async (id: string): Promise<TaskResponse> => {
  const response = await api.get<TaskResponse>(`${TASKS_URL}/${id}`);
  return response.data;
};

/**
 * Create a new task
 */
export const createTask = async (data: CreateTaskRequest): Promise<TaskResponse> => {
  const response = await api.post<TaskResponse>(TASKS_URL, data);
  return response.data;
};

/**
 * Update a task
 */
export const updateTask = async (
  id: string,
  data: UpdateTaskRequest
): Promise<TaskResponse> => {
  const response = await api.patch<TaskResponse>(`${TASKS_URL}/${id}`, data);
  return response.data;
};

/**
 * Delete a task
 */
export const deleteTask = async (id: string): Promise<{ success: boolean }> => {
  const response = await api.delete(`${TASKS_URL}/${id}`);
  return response.data;
};

/**
 * Mark a task as complete
 */
export const completeTask = async (id: string): Promise<TaskResponse> => {
  const response = await api.post<TaskResponse>(`${TASKS_URL}/${id}/complete`);
  return response.data;
};

/**
 * Reopen a completed task
 */
export const reopenTask = async (id: string): Promise<TaskResponse> => {
  const response = await api.post<TaskResponse>(`${TASKS_URL}/${id}/reopen`);
  return response.data;
};

/**
 * Get tasks for a specific client
 */
export const getClientTasks = async (clientId: string): Promise<TasksSimpleResponse> => {
  const response = await api.get<TasksSimpleResponse>(`/clients/${clientId}/tasks`);
  return response.data;
};

// Export all functions as default object for convenience
export default {
  getTasks,
  getTodayTasks,
  getOverdueTasks,
  getTask,
  createTask,
  updateTask,
  deleteTask,
  completeTask,
  reopenTask,
  getClientTasks,
};
